import { Card, Col, InputNumber, Row, Select, notification } from "antd";
import { get } from "lodash";
import Input from "shared/components/Input";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import {
  CONTACT_EXERCISE_FRAME,
  DEPARTMENTS,
  INSURANCE_PLAN_PARAMS_RULES,
  JOB_LIST,
  PROFILE_LIST,
  SOCIAL_REGIMES,
} from "utils/constants";

const ConditionsManager = ({ params, planId, setFieldValue }) => {
  const [updatePlan] = useMutation(graphql.mutations.UPDATE_INSURANCE_PLAN);
  const rules = get(params, "0.rules", {});
  const paramId = get(params, "0.id");

  const getRuleValue = (ruleName) => get(rules, `${ruleName}.value`);

  const setRuleValue = (ruleName, value) => {
    setFieldValue(`params.0.rules.${ruleName}.value`, value);
  };

  const saveParams = async () => {
    try {
      const currentParams = get(params, "0", {});
      const rulesArray = Object.entries(get(currentParams, "rules", {}))
        .filter(([, rule]) => rule && rule.name)
        .map(([, rule]) => ({
          id: rule.id,
          name: rule.name,
          value: rule.value,
        }));

      await updatePlan({
        variables: {
          where: { id: planId },
          data: {
            params: [{ id: paramId, rules: rulesArray }],
          },
        },
      });
    } catch (err) {
      notification.error({ message: "Échec de l'enregistrement des conditions" });
    }
  };

  return (
    <Card title="Conditions d'adhésion" className="m-bottom-16">
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Row gutter={[16, 16]} align="middle">
          <Col span={6}>
            <label className="input__label">Tranche d'âge</label>
          </Col>
          <Col span={9}>
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Min age"
              min={0}
              value={getRuleValue(INSURANCE_PLAN_PARAMS_RULES.MIN_AGE)}
              onChange={(val) =>
                setRuleValue(INSURANCE_PLAN_PARAMS_RULES.MIN_AGE, val)
              }
              onBlur={saveParams}
              addonBefore="Min"
            />
          </Col>
          <Col span={9}>
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Max age"
              min={0}
              value={getRuleValue(INSURANCE_PLAN_PARAMS_RULES.MAX_AGE)}
              onChange={(val) =>
                setRuleValue(INSURANCE_PLAN_PARAMS_RULES.MAX_AGE, val)
              }
              onBlur={saveParams}
              addonBefore="Max"
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} align="middle">
          <Col span={6}>
            <label className="input__label">Régimes sociaux</label>
          </Col>
          <Col span={18}>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder="Sélectionner les régimes..."
              options={Object.entries(SOCIAL_REGIMES).map(([key, value]) => ({
                value,
                label: key.replace(/_/g, " "),
              }))}
              value={getRuleValue(INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES) || []}
              onChange={(value) => {
                setRuleValue(INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES, value);
                setTimeout(saveParams, 100);
              }}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} align="middle">
          <Col span={6}>
            <label className="input__label">Cadre d'exercice</label>
          </Col>
          <Col span={18}>
            <Select
              style={{ width: "100%" }}
              placeholder="Tous"
              allowClear
              options={[
                { value: CONTACT_EXERCISE_FRAME.EMPLOYEE, label: "Salarié" },
                { value: CONTACT_EXERCISE_FRAME.INDEPENDENT, label: "Indépendant" },
              ]}
              value={getRuleValue(INSURANCE_PLAN_PARAMS_RULES.EXERCISE_FRAME) || undefined}
              onChange={(value) => {
                setRuleValue(INSURANCE_PLAN_PARAMS_RULES.EXERCISE_FRAME, value || "");
                setTimeout(saveParams, 100);
              }}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} align="middle">
          <Col span={6}>
            <label className="input__label">Professions</label>

          </Col>
          <Col span={18}>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder="Sélectionner les professions..."
              options={Object.entries(JOB_LIST).map(([key, value]) => ({
                value,
                label: key.replace(/_/g, " "),
              }))}
              value={getRuleValue(INSURANCE_PLAN_PARAMS_RULES.PROFESSION) || []}
              onChange={(value) => {
                setRuleValue(INSURANCE_PLAN_PARAMS_RULES.PROFESSION, value);
                setTimeout(saveParams, 100);
              }}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} align="middle">
          <Col span={6}>
            <label className="input__label">Profils</label>
          </Col>
          <Col span={18}>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder="Sélectionner les profils..."
              options={Object.entries(PROFILE_LIST)
                .map(([key, value]) => ({
                  value,
                  label: key.replace(/_/g, " "),
                }))
                .sort((a, b) => a.label.localeCompare(b.label))}
              value={getRuleValue(INSURANCE_PLAN_PARAMS_RULES.PROFILES) || []}
              onChange={(value) => {
                setRuleValue(INSURANCE_PLAN_PARAMS_RULES.PROFILES, value);
                setTimeout(saveParams, 100);
              }}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} align="middle">
          <Col span={6}>
            <label className="input__label">Départements</label>
          </Col>
          <Col span={18}>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder="Sélectionner les départements..."
              maxTagCount="responsive"
              showSearch
              options={DEPARTMENTS.map((value) => ({ value, label: value }))}
              value={getRuleValue(INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS) || []}
              onChange={(value) => {
                setRuleValue(INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS, value);
                setTimeout(saveParams, 100);
              }}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} align="middle">
          <Col span={6}>
            <label className="input__label">Codes postaux</label>
          </Col>
          <Col span={18}>
            <Select
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Saisir les codes postaux..."
              maxTagCount="responsive"
              tokenSeparators={[",", " "]}
              value={getRuleValue(INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES) || []}
              onChange={(value) => {
                const valid = value.filter((v) => /^\d{5}$/.test(v));
                if (valid.length !== value.length) {
                  notification.warning({ message: "Zip codes must be 5 digits" });
                }
                setRuleValue(INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES, valid);
                setTimeout(saveParams, 100);
              }}
            />
          </Col>
        </Row>
      </div>
    </Card>
  );
};

export default ConditionsManager;
